import { useEffect, useMemo, useState } from "react";
import { focusBlocks, type TaskDto } from "@habitos/shared";

const BREAK_IDEAS = [
  "Levántate y estira los brazos hacia arriba.",
  "Toma un vaso de agua.",
  "Gira los hombros y el cuello despacio.",
  "Camina un poco por la casa.",
  "Respira hondo cinco veces.",
  "Mira por la ventana, lejos de la pantalla.",
];

/** Lets the user know a block ended even if they looked away; best effort. */
async function signal(text: string) {
  navigator.vibrate?.([200, 100, 200]);
  if (document.visibilityState === "visible") return;
  if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
  const registration = await navigator.serviceWorker?.getRegistration();
  await registration?.showNotification(text, { tag: "focus-timer" });
}

/**
 * Guides a focus task block by block: work, break, work… Time is kept as an end
 * timestamp rather than a countdown, so it stays right when the phone sleeps the page.
 */
export function FocusTimer({ task, onClose, onFinish }: { task: TaskDto; onClose: () => void; onFinish: () => void }) {
  const blocks = useMemo(() => focusBlocks(task), [task]);
  const [index, setIndex] = useState(0);
  const [endsAt, setEndsAt] = useState(() => Date.now() + blocks[0]!.minutes * 60_000);
  const [pausedLeft, setPausedLeft] = useState<number | null>(null);
  const [now, setNow] = useState(Date.now());
  const [finished, setFinished] = useState(false);

  const block = blocks[index]!;
  const left = pausedLeft ?? Math.max(0, endsAt - now);
  const breakIdea = BREAK_IDEAS[index % BREAK_IDEAS.length];
  const workBlocks = blocks.filter((b) => b.kind === "work").length;
  const workNumber = blocks.slice(0, index + 1).filter((b) => b.kind === "work").length;

  useEffect(() => {
    if (pausedLeft !== null || finished) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [pausedLeft, finished]);

  useEffect(() => {
    if (finished || pausedLeft !== null || left > 0) return;
    const next = blocks[index + 1];
    if (!next) {
      setFinished(true);
      void signal(`¡Terminaste “${task.title}”!`);
      return;
    }
    setIndex(index + 1);
    setEndsAt(Date.now() + next.minutes * 60_000);
    void signal(next.kind === "break" ? "Pausa: levántate y estírate" : `De vuelta a “${task.title}”`);
  }, [left, finished, pausedLeft, blocks, index, task.title]);

  const togglePause = () => {
    if (pausedLeft === null) {
      setPausedLeft(left);
    } else {
      setEndsAt(Date.now() + pausedLeft);
      setNow(Date.now());
      setPausedLeft(null);
    }
  };

  const skip = () => {
    setEndsAt(Date.now());
    setNow(Date.now());
    setPausedLeft(null);
  };

  const minutes = Math.floor(left / 60_000);
  const seconds = Math.floor((left % 60_000) / 1000);
  const progress = 1 - left / (block.minutes * 60_000);

  return (
    <div className="focus-overlay" role="dialog" aria-modal="true" aria-label={`Concentración: ${task.title}`}>
      <div className={`focus-card ${finished ? "work" : block.kind}`}>
        <p className="muted">{task.emoji} {task.title}</p>
        {finished ? (
          <>
            <h1>¡Listo!</h1>
            <p>Terminaste todos los bloques. ¿La marcamos como hecha?</p>
            <button className="btn primary" onClick={onFinish}>Marcar como hecha</button>
            <button className="link" onClick={onClose}>Cerrar</button>
          </>
        ) : (
          <>
            <h1>{block.kind === "work" ? `Bloque ${workNumber} de ${workBlocks}` : "Pausa"}</h1>
            <p>{block.kind === "work" ? "Concéntrate solo en esto." : breakIdea}</p>
            <div className="focus-clock" aria-live="off">
              {String(minutes).padStart(2, "0")}:{String(seconds).padStart(2, "0")}
            </div>
            <div className="progress-track">
              <div className="progress-fill" style={{ width: `${progress * 100}%` }} />
            </div>
            <div className="row gap focus-actions">
              <button className="btn" onClick={togglePause}>{pausedLeft === null ? "Pausar" : "Seguir"}</button>
              <button className="btn" onClick={skip}>{block.kind === "break" ? "Volver ya" : index === blocks.length - 1 ? "Terminar" : "Ir a la pausa"}</button>
            </div>
            <button className="link" onClick={onClose}>Salir</button>
          </>
        )}
      </div>
    </div>
  );
}
